import { useMemo } from 'react';

interface EmbedPlayerProps {
  url: string;
  accentColor: string;
  compact?: boolean;
  autoplay?: boolean;
}

// Extract YouTube video ID
function extractYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

// Extract Spotify embed info
function extractSpotifyEmbed(url: string): { type: string; id: string } | null {
  // open.spotify.com/track/ID, /album/ID, /playlist/ID, /episode/ID
  const m = url.match(/open\.spotify\.com\/(track|album|playlist|episode|artist)\/([a-zA-Z0-9]+)/);
  if (m) return { type: m[1], id: m[2] };
  // spotify:track:ID format
  const m2 = url.match(/spotify:(track|album|playlist|episode|artist):([a-zA-Z0-9]+)/);
  if (m2) return { type: m2[1], id: m2[2] };
  return null;
}

// Extract SoundCloud URL (just validate it's a soundcloud URL)
function isSoundCloudUrl(url: string): boolean {
  return /soundcloud\.com\/.+\/.+/i.test(url);
}

// Extract niconico video ID
function extractNiconicoId(url: string): string | null {
  const m = url.match(/(?:nicovideo\.jp\/watch|nico\.ms)\/(sm\d+|nm\d+|\d+)/);
  return m ? m[1] : null;
}

export type EmbedType = 'youtube' | 'spotify' | 'soundcloud' | 'niconico' | 'none';

export function detectEmbedType(url: string): EmbedType {
  if (!url) return 'none';
  if (extractYouTubeId(url)) return 'youtube';
  if (extractSpotifyEmbed(url)) return 'spotify';
  if (isSoundCloudUrl(url)) return 'soundcloud';
  if (extractNiconicoId(url)) return 'niconico';
  return 'none';
}

export default function EmbedPlayer({ url, accentColor, compact = false, autoplay = false }: EmbedPlayerProps) {
  const embedContent = useMemo(() => {
    if (!url) return null;

    // YouTube
    const ytId = extractYouTubeId(url);
    if (ytId) {
      const autoplayParam = autoplay ? '&autoplay=1' : '';
      return (
        <div
          className="w-full rounded-xl overflow-hidden"
          style={{
            boxShadow: `0 0 30px ${accentColor}15`,
            aspectRatio: compact ? '16 / 9.9' : '16 / 9.9',
          }}
        >
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1${autoplayParam}`}
            title="YouTube Player"
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    // Spotify
    const spotifyInfo = extractSpotifyEmbed(url);
    if (spotifyInfo) {
      const height = spotifyInfo.type === 'track' || spotifyInfo.type === 'episode'
        ? (compact ? 152 : 152)
        : (compact ? 252 : 352);
      return (
        <div className="w-full rounded-xl overflow-hidden" style={{ boxShadow: `0 0 30px ${accentColor}15` }}>
          <iframe
            src={`https://open.spotify.com/embed/${spotifyInfo.type}/${spotifyInfo.id}?theme=0`}
            title="Spotify Player"
            className="w-full border-0 rounded-xl"
            style={{ height: `${height}px` }}
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
          />
        </div>
      );
    }

    // SoundCloud
    if (isSoundCloudUrl(url)) {
      const height = compact ? 166 : 166;
      const encodedUrl = encodeURIComponent(url);
      const scAutoplay = autoplay ? 'true' : 'false';
      return (
        <div className="w-full rounded-xl overflow-hidden" style={{ boxShadow: `0 0 30px ${accentColor}15` }}>
          <iframe
            title="SoundCloud Player"
            className="w-full border-0"
            style={{ height: `${height}px` }}
            scrolling="no"
            allow="autoplay"
            src={`https://w.soundcloud.com/player/?url=${encodedUrl}&color=${accentColor.replace('#', '')}&auto_play=${scAutoplay}&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`}
          />
        </div>
      );
    }

    // niconico
    const nicoId = extractNiconicoId(url);
    if (nicoId) {
      return (
        <div
          className="w-full rounded-xl overflow-hidden"
          style={{
            boxShadow: `0 0 30px ${accentColor}15`,
            aspectRatio: compact ? '16 / 9.9' : '16 / 9.9',
          }}
        >
          <iframe
            src={`https://embed.nicovideo.jp/watch/${nicoId}?persistence=1&oldScript=1&referer=&from=0${autoplay ? '&autoplay=1' : ''}`}
            title="niconico Player"
            className="w-full h-full border-0"
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        </div>
      );
    }

    return null;
  }, [url, accentColor, compact, autoplay]);

  if (!embedContent) return null;

  return embedContent;
}
